import { useState, useEffect, useCallback } from "react"
import { Helmet } from "react-helmet-async"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table"
import { ChevronLeft, ChevronRight, RefreshCw, Loader2, Zap, ArrowRight } from "lucide-react"
import { ReportPreview } from "@/components/reports/ReportPreview"
import { getScanTypeDisplayName } from "@/utils/displayNames"
import { Navigation } from "@/components/Navigation"
import { useReportActions } from "@/hooks/useReportActions"
import { ToastContainer } from "@/components/ui/toast"
import { fetchWithRefresh } from "@/utils/fetchWithRefresh"
import { useToast } from "@/hooks/use-toast"
import { ScanProgressTracker } from "@/components/ScanProgressTracker"
import { useAuth } from "@/contexts/AuthContext"
import { Link } from "react-router-dom"

interface Report {
  id: string
  target: string
  scanType: string
  status: "completed" | "failed" | "running" | "queued" | "partially_completed"
  vulnerabilities: number
  securityScore: number
  generatedDate: string
  fileSize: string
  progress?: number
  queue_position?: number
  estimated_wait_minutes?: number
}

interface ReportListResponse {
  scans: any[]
  total: number
  page: number
  per_page: number
}

export default function Reports() {
  const { user, checkAuthStatus } = useAuth()
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toasts, removeToast, toast } = useToast()

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalReports, setTotalReports] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('completed')
  const [perPage] = useState(10)

  // Track active scans for polling
  const [activeScans, setActiveScans] = useState<Set<string>>(new Set())

  // Use shared report actions hook
  const { selectedReport, reportData, handleViewReport, handleDownloadReport, handleClosePreview } = useReportActions(toast)

  const fetchReports = useCallback(async (page = 1, status = 'completed') => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort_by: 'created_at',
        sort_dir: 'desc'
      })

      if (status !== 'all') {
        // When filtering for 'completed', also include 'partially_completed' scans
        if (status === 'completed') {
          params.append('status', 'completed,partially_completed')
        } else {
          params.append('status', status)
        }
      }

      const response = await fetchWithRefresh(`/api/scan/list?${params}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch reports')
      }

      const data: ReportListResponse = await response.json()

      // Helper function to format NMAP URLs for display
      const formatUrlForDisplay = (url: string, scanType: string) => {
        // NMAP scans strip https:// for functionality, but we want to display it
        if (scanType.startsWith('nmap_') && !url.startsWith('http://') && !url.startsWith('https://')) {
          return `https://${url}`
        }
        return url
      }

      // Transform scan data to report format
      const transformedReports: Report[] = data.scans.map((scan: any) => {
        return {
          id: scan.id,
          target: formatUrlForDisplay(scan.url, scan.scan_type),
          scanType: getScanTypeDisplayName(scan.scan_type),
          status: scan.status as "completed" | "failed" | "running" | "queued",
          vulnerabilities: scan.vulnerabilities_count || 0,
          securityScore: scan.security_score || 0,
          generatedDate: (() => {
            try {
              const date = new Date(scan.created_at)
              if (isNaN(date.getTime())) return 'Invalid Date'
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              }).replace(',', ' at')
            } catch {
              return 'Invalid Date'
            }
          })(),
          fileSize: "N/A", // TODO: Calculate or store actual file size
          progress: scan.progress ?? 0
        }
      })

      setReports(transformedReports)
      setTotalReports(data.total || 0)
      setTotalPages(Math.ceil(data.total / perPage))
      setCurrentPage(data.page || 1)
      setError(null)
    } catch (err) {
      setError('Failed to load reports')
      console.error('Error fetching reports:', err)
    } finally {
      setLoading(false)
    }
  }, [perPage])

  // Helper functions (copied from ScanManagement)
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default">Completed</Badge>
      case "partially_completed":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-400">Partially Completed</Badge>
      case "running":
        return <Badge variant="secondary">Running</Badge>
      case "queued":
        return <Badge variant="outline">Queued</Badge>
      case "failed":
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const getScoreBadge = (score: number | null | undefined, status: string) => {
    // For running or queued scans, show pending indicator
    if (status === 'running' || status === 'queued') {
      return <Badge className="bg-gray-100 text-gray-800">-/100</Badge>
    }

    // For completed and partially_completed scans, show actual score
    const finalScore = score || 0
    if (finalScore >= 90) return <Badge className="bg-green-100 text-green-800">{finalScore}/100</Badge>
    if (finalScore >= 70) return <Badge className="bg-yellow-100 text-yellow-800">{finalScore}/100</Badge>
    return <Badge className="bg-red-100 text-red-800">{finalScore}/100</Badge>
  }

  const getVulnBadge = (count: number) => {
    if (count === 0) return <Badge variant="default">Clean</Badge>
    if (count <= 5) return <Badge variant="secondary">{count} Issues</Badge>
    return <Badge variant="destructive">{count} Issues</Badge>
  }


  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRefresh = () => {
    fetchReports(currentPage, statusFilter)
  }

  // Update scan progress in state
  const updateScanProgress = useCallback((scanId: string, progress: number, status: string, security_score?: number, vulnerabilities_count?: number, queue_position?: number, estimated_wait_minutes?: number) => {
    setReports(prevReports =>
      prevReports.map(report => {
        if (report.id === scanId) {
          // If status changed to completed or failed, mark progress accordingly
          if (status === 'completed') {
            return {
              ...report,
              progress: 100,
              status: 'completed' as Report['status'],
              securityScore: security_score ?? report.securityScore,
              vulnerabilities: vulnerabilities_count ?? report.vulnerabilities
            }
          } else if (status === 'failed') {
            return {
              ...report,
              progress: 0,
              status: 'failed' as Report['status'],
              securityScore: security_score ?? report.securityScore,
              vulnerabilities: vulnerabilities_count ?? report.vulnerabilities
            }
          }
          return {
            ...report,
            progress,
            status: status as Report['status'],
            securityScore: security_score ?? report.securityScore,
            vulnerabilities: vulnerabilities_count ?? report.vulnerabilities,
            queue_position,
            estimated_wait_minutes
          }
        }
        return report
      })
    )
  }, [])

  // Handle scan completion
  const handleScanComplete = useCallback((scanId: string) => {
    // Remove from active scans first to prevent duplicate handlers
    setActiveScans(prev => {
      const newSet = new Set(prev)
      const wasActive = newSet.has(scanId)
      newSet.delete(scanId)

      // Only show toast and refresh if this scan was actually being tracked
      if (wasActive) {
        // Refresh reports to get final results
        fetchReports(currentPage, statusFilter)

        // Refresh user data to ensure scan counter is up-to-date
        checkAuthStatus()

        // Show completion toast (only once)
        toast({
          title: "Scan completed",
          description: "Your security report is ready.",
        })
      }

      return newSet
    })
  }, [currentPage, statusFilter, fetchReports, checkAuthStatus, toast])

  // Handle scan error (e.g., 404 when scan is deleted)
  const handleScanError = useCallback((scanId: string, error: string) => {
    // Remove from active scans immediately
    setActiveScans(prev => {
      const newSet = new Set(prev)
      newSet.delete(scanId)
      return newSet
    })

    // Remove from reports list immediately (for instant UI update)
    setReports(prevReports => prevReports.filter(report => report.id !== scanId))

    // Refresh reports list to ensure consistency
    fetchReports(currentPage, statusFilter)
  }, [currentPage, statusFilter, fetchReports])

  // Track active scans
  useEffect(() => {
    const activeScanIds = reports
      .filter(report => report.status === 'running' || report.status === 'queued')
      .map(report => report.id)

    setActiveScans(new Set(activeScanIds))
  }, [reports])

  // Effects
  useEffect(() => {
    fetchReports(currentPage, statusFilter)
  }, [fetchReports, currentPage, statusFilter])

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Helmet>
        <title>Reports | Fusegu Security</title>
        <meta name="description" content="View and download your security assessment reports and vulnerability findings." />
      </Helmet>
      <Navigation />
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
        <div className="mx-auto grid w-full max-w-6xl gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Security Reports</h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                View and download generated security assessment reports
              </p>
            </div>
            <Button
              onClick={() => window.location.href = '/scans'}
              className="w-full sm:w-auto"
            >
              Generate New Report
            </Button>
          </div>

          {/* Upgrade Banner for Trial and None Tier Users */}
          {(user?.subscription_tier === 'trial' || user?.subscription_tier === 'none') && (
            <Card className="border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-sm">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Zap className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Upgrade to Pro for 50 scans per day
                      </p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">
                        Get unlimited access and priority support
                      </p>
                    </div>
                  </div>
                  <Link to="/upgrade">
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md hover:shadow-lg transition-all"
                    >
                      Upgrade Now
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Security Reports</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {totalReports} total reports
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={statusFilter} onValueChange={handleStatusFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="running">Running</SelectItem>
                      <SelectItem value="queued">Queued</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleRefresh}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                    aria-label="Refresh reports"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading && reports.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p>Loading reports...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <Button onClick={handleRefresh} variant="outline">
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No reports found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {statusFilter !== 'all' && statusFilter !== 'completed' && 'Try changing the status filter or '}
                    <a href="/scans" className="text-primary hover:underline">Run a scan</a> to generate your first report.
                  </p>
                </div>
              ) : (
                <>
                  {/* Mobile: Card view */}
                  <div className="md:hidden space-y-4">
                    {reports.map((report) => {
                      const progress = report.progress ?? 0
                      const isActive = report.status === 'running' || report.status === 'queued'

                      return (
                        <Card key={report.id} className="p-4">
                          <div className="space-y-3">
                            {/* Target URL */}
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Target</p>
                              <p className="text-sm font-medium break-all">{report.target}</p>
                            </div>

                            {/* Scan Type & Status */}
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Scan Type</p>
                                <Badge variant="outline">{report.scanType}</Badge>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Status</p>
                                <div className="flex items-center gap-2">
                                  {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
                                  {getStatusBadge(report.status)}
                                </div>
                              </div>
                            </div>

                            {/* Progress (if active) */}
                            {(report.status === 'queued' || isActive) && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Progress</p>
                                {report.status === 'queued' && report.queue_position !== undefined ? (
                                  <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">
                                      Position: {report.queue_position + 1}
                                    </p>
                                    {report.estimated_wait_minutes !== undefined && (
                                      <p className="text-xs text-muted-foreground">
                                        {report.estimated_wait_minutes > 0
                                          ? `~${report.estimated_wait_minutes} min wait`
                                          : 'Starting soon'}
                                      </p>
                                    )}
                                  </div>
                                ) : isActive ? (
                                  <div className="space-y-1">
                                    <Progress value={progress} className="h-2" />
                                    <p className="text-xs text-muted-foreground">{progress}%</p>
                                  </div>
                                ) : null}
                              </div>
                            )}

                            {/* Security Score & Issues */}
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Security Score</p>
                                {getScoreBadge(report.securityScore, report.status)}
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Issues</p>
                                {getVulnBadge(report.vulnerabilities)}
                              </div>
                            </div>

                            {/* Generated Date */}
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Generated</p>
                              <p className="text-sm">{report.generatedDate}</p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                              <Button
                                className="flex-1"
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewReport(report.id)}
                                disabled={report.status !== "completed" && report.status !== "partially_completed"}
                              >
                                View
                              </Button>
                              <Button
                                className="flex-1"
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadReport(report.id)}
                                disabled={report.status !== "completed" && report.status !== "partially_completed"}
                              >
                                Download
                              </Button>
                            </div>
                          </div>
                        </Card>
                      )
                    })}
                  </div>

                  {/* Desktop: Table view */}
                  <div className="hidden md:block rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Target</TableHead>
                          <TableHead>Scan Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Security Score</TableHead>
                          <TableHead>Issues</TableHead>
                          <TableHead>Generated</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.map((report) => {
                          const progress = report.progress ?? 0
                          const isActive = report.status === 'running' || report.status === 'queued'

                          return (
                            <TableRow key={report.id}>
                              <TableCell className="font-medium">
                                {report.target}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{report.scanType}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
                                  {getStatusBadge(report.status)}
                                </div>
                              </TableCell>
                              <TableCell>
                                {report.status === 'queued' && report.queue_position !== undefined ? (
                                  <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">
                                      Position: {report.queue_position + 1}
                                    </p>
                                    {report.estimated_wait_minutes !== undefined && (
                                      <p className="text-xs text-muted-foreground">
                                        {report.estimated_wait_minutes > 0
                                          ? `~${report.estimated_wait_minutes} min wait`
                                          : 'Starting soon'}
                                      </p>
                                    )}
                                  </div>
                                ) : isActive ? (
                                  <div className="space-y-1">
                                    <Progress value={progress} className="h-2 w-24" />
                                    <p className="text-xs text-muted-foreground">{progress}%</p>
                                  </div>
                                ) : report.status === 'completed' || report.status === 'partially_completed' ? (
                                  <span className="text-sm text-muted-foreground">Complete</span>
                                ) : report.status === 'failed' ? (
                                  <span className="text-sm text-destructive">Failed</span>
                                ) : (
                                  <span className="text-sm text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {getScoreBadge(report.securityScore, report.status)}
                              </TableCell>
                              <TableCell>
                                {getVulnBadge(report.vulnerabilities)}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {report.generatedDate}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewReport(report.id)}
                                    disabled={report.status !== "completed" && report.status !== "partially_completed"}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadReport(report.id)}
                                    disabled={report.status !== "completed" && report.status !== "partially_completed"}
                                  >
                                    Download
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalReports)} of {totalReports} reports
                      </p>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          <span className="hidden sm:inline">Previous</span>
                        </Button>
                        <span className="text-xs sm:text-sm px-2">
                          {currentPage} / {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                        >
                          <span className="hidden sm:inline">Next</span>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {selectedReport && reportData && (
        <ReportPreview
          reportData={reportData}
          onClose={handleClosePreview}
          onDownload={() => handleDownloadReport(selectedReport)}
        />
      )}

      {/* Scan Progress Trackers - invisible components that poll active scans */}
      {Array.from(activeScans).map(scanId => (
        <ScanProgressTracker
          key={scanId}
          scanId={scanId}
          onUpdate={updateScanProgress}
          onComplete={handleScanComplete}
          onError={handleScanError}
          toast={toast}
        />
      ))}
    </div>
  )
}